export interface User {
  id: string; // GUID as string
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[]; // Changed from single role to array of roles
  isActive: boolean;
  managerId?: string; // GUID as string
  roleTemplateId?: string; // GUID as string
  companyId?: string; // GUID as string
  businessUnitId?: string; // GUID as string
  departmentId?: string; // GUID as string (deprecated - use departmentIds)
  departmentIds?: string[]; // GUIDs as strings (many-to-many relationship)
  departmentName?: string; // Deprecated - use departmentNames
  departmentNames?: string[]; // Department names for display/authorization helpers
  // Role template change acknowledgment tracking
  pendingRoleTemplateId?: string; // GUID as string
  roleTemplateChangeManagerAcknowledgedAt?: string; // ISO date string
  roleTemplateChangeEmployeeAcknowledgedAt?: string; // ISO date string
  /** UTC when the user last signed in (ISO date string). Avoids querying audit events for "last seen". */
  lastLoginAt?: string;
  /** HR-sourced; date-only semantics in org timezone for display. */
  dateOfBirth?: string;
  /** HR-sourced start date; date-only semantics in org timezone for display. */
  dateOfEngagement?: string;
  /** Last calendar day of employment; set when the person leaves. */
  dateOfExit?: string;
  /** IANA time zone for on-screen timestamps. Null/undefined means organisation time. */
  preferredIanaTimeZoneId?: string | null;
}

// Auth types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  expiresAt?: string;
  user: User;
}

export interface TestUser {
  username: string;
  password: string;
  fullName: string;
  role: string;
  description: string;
}

export type OrganogramView = 'team' | 'department' | 'businessunit' | 'all';

export interface OrganogramNode {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roleTemplateName?: string;
  departmentName?: string;
  businessUnitName?: string;
  isActive: boolean;
  children: OrganogramNode[];
}

// Check-in types
export type CheckInFrequency = 'Weekly' | 'Fortnightly' | 'Monthly';
export type CheckInStatus = 'Pending' | 'Completed' | 'Missed';

export interface EmployeeCheckInConfig {
  id: string;
  managerId: string;
  employeeId: string;
  employeeName: string;
  frequency: CheckInFrequency;
  nextCheckInDueDate?: string;
  isActive: boolean;
  /** True when the employee's manager has opted out of built-in check-ins. */
  managerOptedOutOfBuiltInCheckIns?: boolean;
}

export interface ManagerCheckInPreference {
  usesBuiltInCheckIns: boolean;
  checkInsOptOutAcknowledgedAt?: string;
  acknowledgmentPoints?: string[];
  canManagePreference: boolean;
  mustReaffirmAcknowledgment?: boolean;
}

export interface EmployeeCheckIn {
  id: string;
  managerId: string;
  employeeId: string;
  managerName: string;
  employeeName: string;
  dueDate: string;
  status: string;
  managerSchedulingAcknowledgedAt?: string;
  employeeSchedulingAcknowledgedAt?: string;
  managerOccurrenceConfirmedAt?: string;
  employeeOccurrenceConfirmedAt?: string;
  /** When the check-in actually occurred (ISO date). Set when manager records Occurred; null when missed. */
  actualOccurrenceDate?: string | null;
  managerNotes?: string;
  employeeNotes?: string;
}

export interface EmployeeCheckInDashboard {
  nextCheckIn?: EmployeeCheckIn;
  awaitingEmployeeAction: number;
  overdueCount: number;
  checkInStreak: number;
  completionRate: number;
  daysUntilNextCheckIn?: number;
  lateCount: number;
  missedCount: number;
  /** True when the employee's manager has opted out of built-in check-ins. */
  managerOptedOutOfBuiltInCheckIns?: boolean;
}

export interface ManagerCheckInDashboard {
  recentCompletedCheckIns?: EmployeeCheckIn[];
  needsSchedulingAcknowledgment: EmployeeCheckIn[];
  upcomingCheckIns: EmployeeCheckIn[];
  overdueCheckIns: EmployeeCheckIn[];
  awaitingManagerAction: number;
}

export interface ManagerCheckInSummary {
  managerId: string;
  managerName: string;
  directReportCount: number;
  checkInsConfiguredCount: number;
  overdueCount: number;
  missedCount: number;
  lateCount: number;
  totalCheckIns: number;
  hasOptedOutOfCheckIns?: boolean;
  checkInsOptOutAcknowledgedAt?: string;
}

export interface AdminCheckInDashboard {
  managerSummaries: ManagerCheckInSummary[];
  totalOverdue: number;
  totalLate: number;
  totalMissed: number;
  totalNeedsScheduling: number;
}

export interface CheckInAuditEvent {
  id: string;
  eventType: string;
  actorId: string;
  actorName: string;
  eventAt: string;
  details?: string;
  metadata?: string;
}

export interface ReviewAuditEvent {
  id: string;
  eventType: string;
  actorId: string;
  actorName: string;
  eventAt: string;
  details?: string;
  metadata?: string;
}

export interface LeaveAuditEvent {
  id: string;
  leaveRequestId?: string;
  userId: string;
  userName: string;
  eventType: string | number;
  actorId: string;
  actorName: string;
  eventAt: string;
  details?: string;
  metadata?: string;
}

export interface ExpenseClaimAuditEvent {
  id: string;
  expenseClaimId: string;
  userId: string;
  userName: string;
  eventType: string | number;
  actorId: string;
  actorName: string;
  eventAt: string;
  details?: string;
  metadata?: string;
}

export interface ConsolidatedAuditEvent {
  id: string;
  eventType: string;
  eventCategory: string; // "CheckIn", "Review", "User", "IDP", "Leave", "ExpenseClaim"
  actorId: string;
  actorName: string;
  eventAt: string;
  details?: string;
  metadata?: string;
  checkInId?: string;
  reviewId?: string;
  targetUserId?: string;
  individualDevelopmentPlanId?: string;
  leaveRequestId?: string;
  expenseClaimId?: string;
}

export interface UserAuditEvent {
  id: string;
  userId: string;
  userName: string;
  eventType: string;
  actorId: string;
  actorName: string;
  eventAt: string;
  details?: string;
  metadata?: string;
}

export enum RoleTemplateState {
  Draft = 1,
  PendingApproval = 2,
  Approved = 3,
}

// My Outstanding Tasks (unified "My tasks" page)
export interface ReviewTaskItem {
  reviewId: string;
  reviewCycleName: string;
  employeeId: string;
  employeeName: string;
  managerId?: string;
  managerName?: string;
  status: string;
}

export interface CheckInTaskItem {
  checkInId: string;
  employeeName: string;
  managerName: string;
  dueDate: string;
  taskKind: string;
  status: string;
}

export interface CheckInFrequencyTaskItem {
  employeeId: string;
  employeeName: string;
}

export interface PeerTaskItem {
  reviewId: string;
  employeeId: string;
  employeeName: string;
  managerId?: string;
  managerName?: string;
  peerId: string;
  peerName: string;
  peerEmail?: string | null;
}

export interface IdpTaskItem {
  idpId: string;
  employeeName: string;
}

export interface RoleTemplateTaskItem {
  userId: string;
  userName: string;
}

export interface LeaveRequestTaskItem {
  id: string;
  requesterId: string;
  requesterName?: string;
  leaveTypeId: string;
  leaveTypeName?: string;
  fromDate: string;
  toDate: string;
  status: string;
}

export interface ExpenseApprovalTaskItem {
  claimId: string;
  requesterName: string;
  total: number;
  submittedAtUtc?: string | null;
}

export interface MyOutstandingTasks {
  selfReviewPending: ReviewTaskItem[];
  managerReviewPending: ReviewTaskItem[];
  checkInTasks: CheckInTaskItem[];
  checkInFrequencyNotSet: CheckInFrequencyTaskItem[];
  leaveApprovalPending: LeaveRequestTaskItem[];
  expenseApprovalPending?: ExpenseApprovalTaskItem[];
  peerFeedbackPending: PeerTaskItem[];
  /** Peers who have not submitted on reviews you manage (manager chase list). */
  teamPeerFeedbackPending?: PeerTaskItem[];
  idpApprovalPending: IdpTaskItem[];
  roleTemplateAcknowledgmentPending: RoleTemplateTaskItem[];
  totalCount: number;
}

// Expense claims
export interface ExpenseVatOptionDto {
  vatRate: number;
  label: string;
  currencySymbol: string;
}

export interface ExpenseCategory {
  id: string;
  glCode: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ExpenseClaimLineItemDto {
  id: string;
  expenseClaimId: string;
  description: string;
  expenseCategoryId: string;
  expenseCategoryName?: string | null;
  expenseCategoryGlCode?: string | null;
  amount: number;
  sortOrder: number;
}

export interface ExpenseClaimDto {
  id: string;
  requesterId: string;
  requesterName?: string | null;
  /** Set for organisation report only. */
  requesterDepartmentName?: string | null;
  createdByUserId?: string | null;
  createdByName?: string | null;
  claimDate: string;
  payee: string;
  poNumber?: string | null;
  status: string;
  vatRate: number;
  subtotal: number;
  vatAmount: number;
  total: number;
  notes?: string | null;
  submittedAtUtc?: string | null;
  approvedById?: string | null;
  approvedByName?: string | null;
  approvedAtUtc?: string | null;
  rejectedById?: string | null;
  rejectedByName?: string | null;
  rejectedAtUtc?: string | null;
  rejectedReason?: string | null;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
  lineItems: ExpenseClaimLineItemDto[];
}

export interface CreateExpenseClaimLineItemDto {
  description: string;
  expenseCategoryId: string;
  amount: number;
  sortOrder: number;
}

export interface CreateExpenseClaimDto {
  claimDate: string;
  payee: string;
  poNumber?: string | null;
  vatRate: number;
  notes?: string | null;
  lineItems: CreateExpenseClaimLineItemDto[];
  onBehalfOfEmployeeId?: string | null;
}

export interface UpdateExpenseClaimDto {
  claimDate: string;
  payee: string;
  poNumber?: string | null;
  vatRate: number;
  notes?: string | null;
  lineItems: CreateExpenseClaimLineItemDto[];
}

export interface ExpenseClaimDocumentDto {
  id: string;
  expenseClaimId: string;
  expenseClaimLineItemId?: string | null;
  uploadedById: string;
  uploadedByName: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  uploadedAt: string;
}

// Export IDP types
export * from './idp';

export interface CreateHighFiveDto {
  recipientId: string;
  reasonContext: string;
}

export interface UpdateHighFiveDto {
  reasonContext: string;
}

export interface HighFiveItemDto {
  id: string;
  senderId: string;
  senderName: string;
  recipientId: string;
  recipientName: string;
  reasonContext: string;
  givenAtUtc: string;
  highFiveWeekId: string;
}

export interface HighFiveWeekDto {
  weekId: string;
  weekStartDate: string;
  weekEndDate: string;
  status: 'Draft' | 'Published';
  publishedAtUtc?: string | null;
  highFives: HighFiveItemDto[];
}

export interface HighFiveLeaderboardRowDto {
  userId: string;
  userName: string;
  rank: number;
  totalHighFives: number;
  /** High fives received in that leaderboard snapshot’s published week only (not cumulative; not the admin draft week). */
  highFivesThisPublishedWeek?: number;
  previousRank?: number | null;
  movement: number;
  movementType: 'up' | 'down' | 'same' | 'new';
}

export interface HighFiveLeaderboardDto {
  weekId: string;
  weekStartDate: string;
  weekEndDate: string;
  publishedAtUtc?: string | null;
  rows: HighFiveLeaderboardRowDto[];
}

export interface HighFiveRecipientOptionDto {
  userId: string;
  fullName: string;
}

export interface AgentPageContext {
  pageId: string;
  pageTitle?: string;
  route: string;
  viewMode?: string;
  filters?: Record<string, string>;
  visibleSections?: string[];
  suggestedPrompts?: string[];
}

export interface AgentChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  error?: string;
}
